import { AlertTriangle, ShieldAlert } from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { BarList, ColumnChart, Heatmap, LineChart, Meter, type ChartDatum, type SeriesDef } from "@/components/ui/chart";
import type { TabProps } from "@/features/report-lines";
import {
  DataTable, Figures, KpiRow, KpiTile, NONE, ReportSection, Segmented, ShareBar, tableExport, type DataColumn,
} from "@/features/report-kit";
import { stageText } from "@/lib/contacts";
import { categoryTerm } from "@/lib/dispositions";
import { useReport } from "@/lib/queries";
import {
  BLOCK_REASON_LABELS, DID_STRATEGY_LABELS, MODE_LABELS, PROBLEM_LABELS, STARTER_LABELS, binLabel, bucketLabel,
  bucketTitle, delta, fmtCompact, fmtCount, fmtPct, fmtSeconds, labelOf,
} from "@/lib/reports";
import type { DialCounts, DialingReport, LeadReport, ListReport, OutcomeReport } from "@/lib/types";
import { formatDateTime, formatPhoneNumber } from "@/lib/utils";

/** The dial outcomes, each always in the same slot. */
const OUTCOME_SERIES: SeriesDef[] = [
  { key: "contacts", label: "Reached a rep", slot: 1 },
  { key: "voicemail", label: "Voicemail", slot: 2 },
  { key: "no_answer", label: "No answer", slot: 3 },
  { key: "busy", label: "Busy", slot: 4 },
  { key: "failed", label: "Failed", slot: 5 },
  { key: "abandoned", label: "Abandoned", slot: 6 },
];

/** Outcome categories (the catalog's), each always in the same slot. */
const CATEGORY_SERIES: SeriesDef[] = [
  { key: "success", label: "Success", slot: 1 },
  { key: "contact", label: "Contact", slot: 2 },
  { key: "no_contact", label: "No contact", slot: 3 },
  { key: "compliance", label: "Compliance", slot: 4 },
  { key: "uncategorized", label: "No category", slot: "other" },
];

/** A lead's stage group (ADR-046), each always in the same slot. */
const STAGE_SERIES: SeriesDef[] = [
  { key: "working", label: "Working", slot: 1 },
  { key: "scheduled", label: "Booked", slot: 2 },
  { key: "exit", label: "Finished", slot: 3 },
  { key: "none", label: "No stage", slot: "other" },
];

/** A list row's state, grouped the way a manager asks about a list. */
const LIST_GROUPS: readonly { key: string; label: string; slot: SeriesDef["slot"]; states: readonly string[] }[] = [
  { key: "working", label: "Still to dial", slot: 1, states: ["fresh", "retrying"] },
  { key: "live", label: "On a call now", slot: 2, states: ["offered", "in_flight"] },
  { key: "waiting", label: "Waiting for an outcome", slot: 3, states: ["needs_disposition"] },
  { key: "finished", label: "Finished", slot: 4, states: ["done", "exhausted", "retired", "dnc"] },
  { key: "blocked", label: "Blocked", slot: 5, states: ["blocked"] },
];

function counts(c: DialCounts): Record<string, number> {
  return {
    contacts: c.contacts,
    voicemail: c.voicemail,
    no_answer: c.no_answer,
    busy: c.busy,
    failed: c.failed,
    abandoned: c.abandoned,
    contact_rate: c.contact_rate ?? NaN,
    answer_rate: c.answer_rate ?? NaN,
  };
}

/** A campaign the history names and the configuration no longer has, told apart by its id. */
function deletedName(id: string): string {
  return `Deleted campaign · ${id.slice(0, 6)}`;
}

type CampaignRow = {
  id: string;
  name: string;
  dial: DialCounts | null;
  outcomes: OutcomeReport["by_campaign"][number] | null;
  list: ListReport["campaigns"][number] | null;
  abandonment: DialingReport["abandonment"][number] | null;
};

const CAMPAIGN_COLUMNS: DataColumn<CampaignRow>[] = [
  { key: "name", header: "Campaign", value: (r) => r.name },
  { key: "attempts", header: "Dials", align: "right", value: (r) => r.dial?.attempts ?? 0, render: (r) => fmtCount(r.dial?.attempts ?? 0) },
  { key: "answer_rate", header: "Answer %", align: "right", value: (r) => r.dial?.answer_rate ?? null, render: (r) => fmtPct(r.dial?.answer_rate) },
  {
    key: "contact_rate",
    header: "Contact %",
    align: "right",
    value: (r) => r.dial?.contact_rate ?? null,
    render: (r) => (
      <>
        {fmtPct(r.dial?.contact_rate)}
        <ShareBar value={r.dial?.contact_rate ?? null} />
      </>
    ),
  },
  { key: "voicemail", header: "Voicemails", align: "right", value: (r) => r.dial?.voicemail ?? 0 },
  { key: "outcomes", header: "Outcomes", align: "right", value: (r) => r.outcomes?.total ?? 0 },
  { key: "success", header: "Successes", align: "right", value: (r) => r.outcomes?.success ?? 0 },
  { key: "success_rate", header: "Success %", align: "right", value: (r) => r.outcomes?.success_rate ?? null, render: (r) => fmtPct(r.outcomes?.success_rate) },
  { key: "on_list", header: "On the list", align: "right", value: (r) => r.list?.total ?? null, render: (r) => fmtCount(r.list?.total) },
  { key: "due_now", header: "Due now", align: "right", value: (r) => r.list?.due_now ?? null, render: (r) => fmtCount(r.list?.due_now) },
  { key: "penetration", header: "Dialed at least once", align: "right", value: (r) => r.list?.penetration ?? null, render: (r) => fmtPct(r.list?.penetration) },
  {
    key: "abandon_30",
    header: "Abandon % (30 d)",
    align: "right",
    title: "Abandoned over live answers, the 30 days to the report's end: the rule's limit is 3%",
    value: (r) => r.abandonment?.abandon_rate ?? null,
    render: (r) =>
      r.abandonment?.over_limit ? (
        <Badge tone="danger" size="sm">
          <ShieldAlert className="size-3" />
          {fmtPct(r.abandonment.abandon_rate)}
        </Badge>
      ) : (
        fmtPct(r.abandonment?.abandon_rate)
      ),
  },
];

type Stepped = DialingReport["cadence_steps"][number];
const STEP_COLUMNS: DataColumn<Stepped>[] = [
  { key: "campaign", header: "Campaign", value: (r) => r.campaign_name ?? (r.campaign_id ? deletedName(r.campaign_id) : "—") },
  { key: "cadence", header: "Cadence", value: (r) => r.cadence ?? "—" },
  { key: "cycle", header: "Cycle", align: "right", value: (r) => r.cycle },
  { key: "step", header: "Step", align: "right", value: (r) => r.step },
  { key: "attempts", header: "Dials", align: "right", value: (r) => r.attempts },
  {
    key: "contact_rate",
    header: "Contact %",
    align: "right",
    value: (r) => r.contact_rate,
    render: (r) => (
      <>
        {fmtPct(r.contact_rate)}
        <ShareBar value={r.contact_rate} />
      </>
    ),
  },
  { key: "voicemail", header: "Voicemails", align: "right", value: (r) => r.voicemail },
  { key: "no_answer", header: "No answer", align: "right", value: (r) => r.no_answer },
  { key: "technical_retries", header: "Technical retries", align: "right", value: (r) => r.technical_retries },
];

type CallerId = DialingReport["caller_ids"][number];
const CALLER_ID_COLUMNS: DataColumn<CallerId>[] = [
  {
    key: "did",
    header: "Number",
    value: (r) => r.did,
    render: (r) => (
      <span className="inline-flex items-center gap-2">
        <span className="font-mono text-xs">{formatPhoneNumber(r.did)}</span>
        {r.low_answer_rate && (
          <Badge tone="warning" size="sm" title="Answered at under half the rate of every number in this report">
            <AlertTriangle className="size-3" />
            Low answer rate
          </Badge>
        )}
      </span>
    ),
  },
  { key: "attempts", header: "Dials", align: "right", value: (r) => r.attempts },
  {
    key: "answer_rate",
    header: "Answer %",
    align: "right",
    value: (r) => r.answer_rate,
    render: (r) => (
      <>
        {fmtPct(r.answer_rate)}
        <ShareBar value={r.answer_rate} tone={r.low_answer_rate ? "warning" : "accent"} />
      </>
    ),
  },
  { key: "contact_rate", header: "Contact %", align: "right", value: (r) => r.contact_rate, render: (r) => fmtPct(r.contact_rate) },
  { key: "failed", header: "Failed", align: "right", value: (r) => r.failed },
  {
    key: "top_causes",
    header: "Most common when unanswered",
    value: (r) => Object.entries(r.top_causes).map(([c, n]) => `${c} ${n}`).join(", "),
    render: (r) => (
      <span className="text-xs text-text-muted">
        {Object.entries(r.top_causes)
          .map(([c, n]) => `${c.replaceAll("_", " ").toLowerCase()} ${n}`)
          .join(" · ") || "—"}
      </span>
    ),
    sortable: false,
  },
  { key: "last_used", header: "Last used", value: (r) => r.last_used, render: (r) => formatDateTime(r.last_used) },
];

type Carrier = DialingReport["carriers"][number];
const CARRIER_COLUMNS: DataColumn<Carrier>[] = [
  { key: "label", header: "Trunks, in order", value: (r) => r.label },
  { key: "attempts", header: "Dials", align: "right", value: (r) => r.attempts },
  { key: "failed", header: "Failed", align: "right", value: (r) => r.failed },
  {
    key: "failure_rate",
    header: "Failure %",
    align: "right",
    value: (r) => (r.attempts ? r.failed / r.attempts : null),
    render: (r) => {
      const v = r.attempts ? r.failed / r.attempts : null;
      return (
        <>
          {fmtPct(v)}
          <ShareBar value={v} tone={v != null && v > 0.1 ? "danger" : "accent"} />
        </>
      );
    },
  },
  { key: "answer_rate", header: "Answer %", align: "right", value: (r) => r.answer_rate, render: (r) => fmtPct(r.answer_rate) },
  {
    key: "failure_causes",
    header: "Why they failed",
    value: (r) => Object.entries(r.failure_causes).map(([c, n]) => `${c} ${n}`).join(", "),
    render: (r) => (
      <span className="text-xs text-text-muted">
        {Object.entries(r.failure_causes)
          .map(([c, n]) => `${c.replaceAll("_", " ").toLowerCase()} ${n}`)
          .join(" · ") || "—"}
      </span>
    ),
    sortable: false,
  },
];

type Firm = LeadReport["firms"][number];
const FIRM_COLUMNS: DataColumn<Firm>[] = [
  { key: "label", header: "Firm", value: (r) => r.label },
  { key: "leads", header: "New leads", align: "right", value: (r) => r.leads },
  { key: "dialed", header: "Dialed", align: "right", value: (r) => r.dialed },
  { key: "contacted", header: "Reached", align: "right", value: (r) => r.contacted },
  { key: "contact_rate", header: "Reached %", align: "right", value: (r) => r.contact_rate, render: (r) => fmtPct(r.contact_rate) },
  { key: "working", header: "Working", align: "right", value: (r) => r.working },
  { key: "scheduled", header: "Booked", align: "right", value: (r) => r.scheduled },
  { key: "signed", header: "Signed", align: "right", value: (r) => r.signed },
  {
    key: "sign_rate",
    header: "Signed %",
    align: "right",
    value: (r) => r.sign_rate,
    render: (r) => (
      <>
        {fmtPct(r.sign_rate)}
        <ShareBar value={r.sign_rate} />
      </>
    ),
  },
  { key: "not_qualified", header: "Not qualified", align: "right", value: (r) => r.not_qualified },
];

type HeatMetric = "contact_rate" | "answer_rate" | "attempts";

export function CampaignReportView({ query, filters, compareText, onDrill, onTruncated }: TabProps) {
  const dialing = useReport<DialingReport>("dialing", query);
  const outcomes = useReport<OutcomeReport>("outcomes", query);
  const lists = useReport<ListReport>("lists", query);
  const leads = useReport<LeadReport>("leads", query);
  const d = dialing.data;
  const o = outcomes.data;
  const l = leads.data;
  React.useEffect(() => onTruncated("dialing", Boolean(d?.truncated)), [d?.truncated, onTruncated]);
  React.useEffect(() => onTruncated("outcomes", Boolean(o?.truncated)), [o?.truncated, onTruncated]);
  React.useEffect(() => onTruncated("leads", Boolean(l?.truncated)), [l?.truncated, onTruncated]);
  const [heat, setHeat] = React.useState<HeatMetric>("contact_rate");
  const g = d?.window.granularity ?? "day";

  const points: ChartDatum[] = (d?.series ?? []).map((s) => ({
    key: s.start,
    label: bucketLabel(s.start, g),
    title: bucketTitle(s.start, g),
    values: { ...counts(s), contact_rate: s.contact_rate, answer_rate: s.answer_rate },
  }));

  const campaigns: CampaignRow[] = React.useMemo(() => {
    const byId = new Map<string, CampaignRow>();
    const row = (id: string | null, name: string | null): CampaignRow | null => {
      if (!id) return null;
      let found = byId.get(id);
      if (!found) {
        found = { id, name: name ?? deletedName(id), dial: null, outcomes: null, list: null, abandonment: null };
        byId.set(id, found);
      } else if (name && found.name === deletedName(id)) {
        found.name = name;
      }
      return found;
    };
    for (const c of d?.by_campaign ?? []) {
      const r = row(c.campaign_id, c.campaign_name);
      if (r) r.dial = c;
    }
    for (const c of o?.by_campaign ?? []) {
      const r = row(c.campaign_id, c.campaign_name);
      if (r) r.outcomes = c;
    }
    for (const c of lists.data?.campaigns ?? []) {
      const r = row(c.campaign_id, c.campaign_name);
      if (r) r.list = c;
    }
    for (const c of d?.abandonment ?? []) {
      const r = row(c.campaign_id, c.campaign_name);
      if (r) r.abandonment = c;
    }
    return [...byId.values()];
  }, [d, o, lists.data]);

  const catLabel = (key: string) => (key === "uncategorized" ? "No category" : (categoryTerm(key)?.label ?? key));
  const outcomeRows = o?.by_disposition ?? NONE;
  const exports = {
    series: React.useMemo(
      () =>
        tableExport(
          [
            { key: "start", header: "Period", value: (p: DialingReport["series"][number]) => bucketTitle(p.start, g) },
            { key: "attempts", header: "Dials", value: (p: DialingReport["series"][number]) => p.attempts },
            ...OUTCOME_SERIES.map((s) => ({
              key: s.key,
              header: s.label,
              value: (p: DialingReport["series"][number]) => p[s.key as keyof DialCounts] as number,
            })),
            { key: "contact_rate", header: "Contact rate", value: (p: DialingReport["series"][number]) => p.contact_rate },
            { key: "answer_rate", header: "Answer rate", value: (p: DialingReport["series"][number]) => p.answer_rate },
          ],
          d?.series ?? [],
        ),
      [d?.series, g],
    ),
    campaigns: React.useMemo(() => tableExport(CAMPAIGN_COLUMNS, campaigns), [campaigns]),
    steps: React.useMemo(() => tableExport(STEP_COLUMNS, d?.cadence_steps ?? []), [d?.cadence_steps]),
    callerIds: React.useMemo(() => tableExport(CALLER_ID_COLUMNS, d?.caller_ids ?? []), [d?.caller_ids]),
    carriers: React.useMemo(() => tableExport(CARRIER_COLUMNS, d?.carriers ?? []), [d?.carriers]),
    firms: React.useMemo(() => tableExport(FIRM_COLUMNS, l?.firms ?? []), [l?.firms]),
    outcomes: React.useMemo(
      () =>
        tableExport(
          [
            { key: "label", header: "Outcome", value: (r: (typeof outcomeRows)[number]) => r.label },
            { key: "slug", header: "Slug", value: (r: (typeof outcomeRows)[number]) => r.slug },
            { key: "category", header: "Category", value: (r: (typeof outcomeRows)[number]) => r.category },
            { key: "count", header: "Count", value: (r: (typeof outcomeRows)[number]) => r.count },
            { key: "share", header: "Share", value: (r: (typeof outcomeRows)[number]) => r.share },
            { key: "talk_avg", header: "Avg talk (s)", value: (r: (typeof outcomeRows)[number]) => r.talk_avg },
          ],
          outcomeRows,
        ),
      [outcomeRows],
    ),
    modes: React.useMemo(
      () =>
        tableExport(
          [
            { key: "mode", header: "Mode", value: (r: DialingReport["by_mode"][number]) => labelOf(MODE_LABELS, r.dial_mode) },
            { key: "by", header: "Started by", value: (r: DialingReport["by_mode"][number]) => labelOf(STARTER_LABELS, r.initiated_by) },
            { key: "attempts", header: "Dials", value: (r: DialingReport["by_mode"][number]) => r.attempts },
            { key: "contact_rate", header: "Contact rate", value: (r: DialingReport["by_mode"][number]) => r.contact_rate },
            { key: "answer_rate", header: "Answer rate", value: (r: DialingReport["by_mode"][number]) => r.answer_rate },
          ],
          d?.by_mode ?? [],
        ),
      [d?.by_mode],
    ),
    lists: React.useMemo(
      () =>
        tableExport(
          [
            { key: "name", header: "Campaign", value: (r: ListReport["campaigns"][number]) => r.campaign_name ?? r.campaign_id },
            { key: "total", header: "On the list", value: (r: ListReport["campaigns"][number]) => r.total },
            ...LIST_GROUPS.map((grp) => ({
              key: grp.key,
              header: grp.label,
              value: (r: ListReport["campaigns"][number]) => grp.states.reduce((acc, s) => acc + (r.by_state[s] ?? 0), 0),
            })),
            { key: "due_now", header: "Due now", value: (r: ListReport["campaigns"][number]) => r.due_now },
            { key: "held", header: "Held", value: (r: ListReport["campaigns"][number]) => r.held },
            { key: "penetration", header: "Dialed at least once", value: (r: ListReport["campaigns"][number]) => r.penetration },
            { key: "added", header: "Added in the window", value: (r: ListReport["campaigns"][number]) => r.added_in_window },
          ],
          lists.data?.campaigns ?? [],
        ),
      [lists.data?.campaigns],
    ),
    compliance: React.useMemo(
      () =>
        tableExport(
          [
            { key: "campaign", header: "Campaign", value: (r: DialingReport["abandonment"][number]) => r.campaign_name ?? r.campaign_id },
            { key: "live", header: "Live answers", value: (r: DialingReport["abandonment"][number]) => r.live_answers },
            { key: "abandoned", header: "Abandoned", value: (r: DialingReport["abandonment"][number]) => r.abandoned },
            { key: "rate", header: "Abandon rate", value: (r: DialingReport["abandonment"][number]) => r.abandon_rate },
            { key: "over", header: "Over 3%", value: (r: DialingReport["abandonment"][number]) => (r.over_limit ? "yes" : "no") },
          ],
          d?.abandonment ?? [],
        ),
      [d?.abandonment],
    ),
  };

  const t = d?.totals;
  const p = d?.previous ?? null;
  const loadingDial = dialing.isPending;
  const heatFormat = heat === "attempts" ? fmtCount : fmtPct;

  return (
    <div className="space-y-4">
      <KpiRow>
        <KpiTile
          label="Dials"
          loading={loadingDial}
          value={fmtCompact(t?.attempts)}
          hint={t ? `${fmtCount(t.technical_retries)} technical retries` : undefined}
          delta={delta(t?.attempts, p?.attempts, "count", "neutral")}
          deltaContext={compareText}
          spark={(d?.series ?? []).map((s) => s.attempts)}
        />
        <KpiTile
          label="Contact rate"
          loading={loadingDial}
          value={fmtPct(t?.contact_rate)}
          hint={t ? `${fmtCount(t.contacts)} reached a rep` : undefined}
          delta={delta(t?.contact_rate, p?.contact_rate, "rate", "up")}
          deltaContext={compareText}
          spark={(d?.series ?? []).map((s) => s.contact_rate)}
          info="Dials a rep was connected on, over finished dials."
        />
        <KpiTile
          label="Answer rate"
          loading={loadingDial}
          value={fmtPct(t?.answer_rate)}
          hint="the lead's phone picked up"
          delta={delta(t?.answer_rate, p?.answer_rate, "rate", "up")}
          deltaContext={compareText}
          spark={(d?.series ?? []).map((s) => s.answer_rate)}
          info="A voicemail greeting answers too. Until machine detection is live, an answered call no rep reached counts as no answer."
        />
        <KpiTile
          label="Voicemails"
          loading={loadingDial}
          value={fmtCount(t?.voicemail)}
          hint="messages left"
          delta={delta(t?.voicemail, p?.voicemail, "count", "neutral")}
          deltaContext={compareText}
        />
        <KpiTile
          label="Outcomes"
          loading={outcomes.isPending}
          value={fmtCompact(o?.summary.total)}
          hint={o ? `${fmtPct(o.summary.dispositioned_rate)} of calls got one` : undefined}
          delta={delta(o?.summary.total, o?.previous?.total, "count", "neutral")}
          deltaContext={compareText}
          spark={(o?.series ?? []).map((s) => s.total)}
        />
        <KpiTile
          label="Success rate"
          loading={outcomes.isPending}
          value={fmtPct(o?.summary.success_rate)}
          hint={o ? `${fmtCount(o.summary.success)} successes` : undefined}
          delta={delta(o?.summary.success_rate, o?.previous?.success_rate, "rate", "up")}
          deltaContext={compareText}
          spark={(o?.series ?? []).map((s) => (s.total ? s.success / s.total : null))}
          info="Outcomes in the Success category (Signed, Appointment set, …) over all outcomes."
        />
        <KpiTile
          label="Speed to lead"
          loading={leads.isPending}
          value={fmtSeconds(l?.speed.first_dial_p50)}
          hint={l ? `median to first dial · ${fmtPct(l.speed.within_5_minutes)} within 5 min` : undefined}
          delta={delta(l?.speed.first_dial_p50, l?.previous?.first_dial_p50, "seconds", "down")}
          deltaContext={compareText}
          info="From the lead arriving to the first dial, for new leads the dialer dialed. A lead who called in first is counted apart."
        />
        <KpiTile
          label="New leads"
          loading={leads.isPending}
          value={fmtCompact(l?.speed.leads)}
          hint={l ? `${fmtCount(l.moves.signed)} signed in the window` : undefined}
          delta={delta(l?.speed.leads, l?.previous?.leads, "count", "neutral")}
          deltaContext={compareText}
          spark={(l?.series ?? []).map((s) => s.new)}
        />
      </KpiRow>

      <ReportSection
        id="dialing-over-time"
        title="Dialing over time"
        description="Every dial the dialer placed, by how it ended."
        loading={loadingDial}
        empty={!loadingDial && (t?.attempts ?? 0) === 0 ? "The dialer placed no calls in this window." : false}
        exportData={exports.series}
      >
        <div className="space-y-6">
          <ColumnChart data={points} series={OUTCOME_SERIES} format={fmtCount} ariaLabel="Dials per period by how they ended" />
          <LineChart
            data={points}
            series={[
              { key: "contact_rate", label: "Contact rate", slot: 1 },
              { key: "answer_rate", label: "Answer rate", slot: 2 },
            ]}
            percent
            format={fmtPct}
            ariaLabel="Contact and answer rate per period"
          />
        </div>
      </ReportSection>

      <ReportSection
        id="campaigns"
        title="Campaigns"
        description="Dialing, outcomes, list and compliance side by side. Click one to narrow the report to it."
        loading={loadingDial || outcomes.isPending || lists.isPending}
        exportData={exports.campaigns}
      >
        <DataTable
          rows={campaigns}
          columns={CAMPAIGN_COLUMNS}
          rowKey={(r) => r.id}
          initialSort={{ key: "attempts", dir: "desc" }}
          onRowClick={(r) => onDrill({ campaigns: [r.id] })}
        />
      </ReportSection>

      <div className="grid gap-4 xl:grid-cols-2">
        <ReportSection
          id="outcomes"
          title="Outcomes"
          description="What reps chose after their calls, and on contacts' records."
          loading={outcomes.isPending}
          exportData={exports.outcomes}
          empty={!outcomes.isPending && outcomeRows.length === 0 ? "No outcomes were chosen in this window." : false}
          note={o && o.summary.on_records > 0 ? `${fmtCount(o.summary.on_records)} were set on a contact's record rather than on a call.` : undefined}
        >
          <BarList
            rows={outcomeRows.map((r) => ({
              key: r.slug,
              name: r.label,
              label: r.label,
              sublabel: `${catLabel(r.category ?? "uncategorized")} · ${fmtPct(r.share)}`,
              values: { [r.category ?? "uncategorized"]: r.count },
            }))}
            series={CATEGORY_SERIES.filter((s) => outcomeRows.some((r) => (r.category ?? "uncategorized") === s.key))}
            format={fmtCount}
            ariaLabel="Outcomes, most chosen first"
          />
        </ReportSection>

        <ReportSection
          id="outcomes-over-time"
          title="Outcomes over time"
          description="By the catalog's category."
          loading={outcomes.isPending}
          empty={!outcomes.isPending && (o?.summary.total ?? 0) === 0 ? "No outcomes were chosen in this window." : false}
        >
          <ColumnChart
            data={(o?.series ?? []).map((s) => ({
              key: s.start,
              label: bucketLabel(s.start, o?.window.granularity ?? g),
              title: bucketTitle(s.start, o?.window.granularity ?? g),
              values: { success: s.success, contact: s.contact, no_contact: s.no_contact, compliance: s.compliance, uncategorized: s.uncategorized },
            }))}
            series={CATEGORY_SERIES}
            format={fmtCount}
            ariaLabel="Outcomes per period by category"
          />
        </ReportSection>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <ReportSection
          id="best-time"
          title="Best time to reach leads"
          description="When dials were answered, on the report's clock."
          loading={loadingDial}
          actions={
            <Segmented
              label="Heatmap shows"
              value={heat}
              onChange={(v) => setHeat(v as HeatMetric)}
              options={[
                { id: "contact_rate", label: "Contact %" },
                { id: "answer_rate", label: "Answer %" },
                { id: "attempts", label: "Dials" },
              ]}
            />
          }
        >
          <Heatmap
            cells={(d?.heatmap ?? []).map((c) => ({
              weekday: c.weekday,
              hour: c.hour,
              value: c[heat],
              detail: `${fmtCount(c.attempts)} dials · ${fmtPct(c.contact_rate)} reached`,
            }))}
            format={heatFormat}
            legend={heat === "attempts" ? "More dials" : "Higher"}
            ariaLabel="Dials by weekday and hour"
          />
        </ReportSection>

        <ReportSection
          id="dial-modes"
          title="By dial mode"
          description="Power, preview and manual, and who started each dial."
          loading={loadingDial}
          exportData={exports.modes}
        >
          <BarList
            rows={(d?.by_mode ?? []).map((m) => ({
              key: `${m.dial_mode}:${m.initiated_by}`,
              name: `${labelOf(MODE_LABELS, m.dial_mode)}, ${labelOf(STARTER_LABELS, m.initiated_by)}`,
              label: `${labelOf(MODE_LABELS, m.dial_mode)} · ${labelOf(STARTER_LABELS, m.initiated_by)}`,
              sublabel: `${fmtPct(m.contact_rate)} reached · ${fmtPct(m.answer_rate)} answered`,
              values: counts(m),
            }))}
            series={OUTCOME_SERIES}
            format={fmtCount}
            ariaLabel="Dials by mode and starter"
          />
        </ReportSection>
      </div>

      <ReportSection
        id="cadence-steps"
        title="Cadence steps"
        description="Which calls of a cycle reach people. A technical retry is counted apart from the step it retried."
        loading={loadingDial}
        exportData={exports.steps}
        empty={!loadingDial && (d?.cadence_steps.length ?? 0) === 0 ? "No cadence dials in this window." : false}
      >
        <DataTable rows={d?.cadence_steps ?? []} columns={STEP_COLUMNS} rowKey={(r) => `${r.campaign_id}:${r.cadence}:${r.cycle}:${r.step}`} />
      </ReportSection>

      <div className="grid gap-4 xl:grid-cols-2">
        <ReportSection
          id="caller-ids"
          title="Caller ID health"
          description="Every number the dialer showed. A number answered far less than the rest may be labelled as spam."
          loading={loadingDial}
          exportData={exports.callerIds}
          note={
            d && d.did_strategies.length > 0
              ? `By caller ID choice: ${d.did_strategies
                  .map((s) => `${labelOf(DID_STRATEGY_LABELS, s.strategy)} ${fmtPct(s.contact_rate)} reached (${fmtCount(s.attempts)})`)
                  .join(" · ")}`
              : undefined
          }
        >
          <DataTable rows={d?.caller_ids ?? []} columns={CALLER_ID_COLUMNS} rowKey={(r) => r.did} initialSort={{ key: "attempts", dir: "desc" }} limit={10} />
        </ReportSection>

        <ReportSection
          id="carriers"
          title="Trunks"
          description="Where the calls left, and how often the carrier refused them."
          loading={loadingDial}
          exportData={exports.carriers}
        >
          <DataTable rows={d?.carriers ?? []} columns={CARRIER_COLUMNS} rowKey={(r) => r.label} initialSort={{ key: "attempts", dir: "desc" }} limit={10} />
        </ReportSection>
      </div>

      <ReportSection
        id="dial-lists"
        title="Dial lists"
        description={lists.data ? `Where every list stands now (${formatDateTime(lists.data.as_of)}). Not affected by the window.` : "Where every list stands now."}
        loading={lists.isPending}
        exportData={exports.lists}
        empty={!lists.isPending && (lists.data?.campaigns.length ?? 0) === 0 ? "No campaign has a dial list." : false}
      >
        <BarList
          rows={(lists.data?.campaigns ?? []).map((c) => ({
            key: c.campaign_id,
            name: c.campaign_name ?? deletedName(c.campaign_id),
            label: c.campaign_name ?? deletedName(c.campaign_id),
            sublabel: `${fmtCount(c.due_now)} due now · ${fmtPct(c.penetration)} dialed · ${fmtCount(c.added_in_window)} added`,
            values: Object.fromEntries(LIST_GROUPS.map((grp) => [grp.key, grp.states.reduce((acc, s) => acc + (c.by_state[s] ?? 0), 0)])),
          }))}
          series={LIST_GROUPS.map((grp) => ({ key: grp.key, label: grp.label, slot: grp.slot }))}
          format={fmtCount}
          ariaLabel="Each campaign's list by where its leads stand"
        />
      </ReportSection>

      <div className="grid gap-4 xl:grid-cols-2">
        <ReportSection
          id="speed-to-lead"
          title="Speed to lead"
          description="From a new lead arriving to its first dial."
          loading={leads.isPending}
          empty={!leads.isPending && (l?.speed.leads ?? 0) === 0 ? "No new leads arrived in this window." : false}
        >
          {l && (
            <div className="space-y-4">
              <Figures
                items={[
                  { label: "Median first dial", value: fmtSeconds(l.speed.first_dial_p50), hint: `90% within ${fmtSeconds(l.speed.first_dial_p90)}` },
                  { label: "Within 5 minutes", value: fmtPct(l.speed.within_5_minutes) },
                  { label: "First contact", value: fmtSeconds(l.speed.first_contact_p50), hint: "median" },
                  { label: "Never dialed", value: fmtCount(l.speed.never_dialed) },
                  { label: "Called us first", value: fmtCount(l.speed.called_in_first) },
                ]}
              />
              <ColumnChart
                data={l.speed.bins.map((b) => ({ key: String(b.lo), label: binLabel(b.lo, b.hi), values: { leads: b.leads } }))}
                series={[{ key: "leads", label: "Leads", slot: 1 }]}
                height={170}
                format={fmtCount}
                ariaLabel="New leads by time to first dial"
              />
            </div>
          )}
        </ReportSection>

        <ReportSection
          id="lead-funnel"
          title="Where new leads are now"
          description="The window's new leads by their stage today."
          loading={leads.isPending}
          empty={!leads.isPending && (l?.where_now.length ?? 0) === 0 ? "No new leads arrived in this window." : false}
          note={
            l
              ? `${fmtCount(l.moves.moves)} stage moves in the window; ${fmtCount(l.moves.signed)} into a signed stage${l.moves.days_to_sign_p50 != null ? `, a median ${l.moves.days_to_sign_p50.toFixed(1)} days after the lead arrived` : ""}. Stage history starts 23 September 2026.`
              : undefined
          }
        >
          <BarList
            rows={(l?.where_now ?? []).map((s) => ({
              key: s.stage ?? "none",
              name: stageText(s.stage).label,
              label: stageText(s.stage).label,
              values: { [s.group]: s.count },
            }))}
            series={STAGE_SERIES.filter((s) => (l?.where_now ?? []).some((w) => w.group === s.key))}
            format={fmtCount}
            ariaLabel="New leads by stage"
          />
        </ReportSection>
      </div>

      <ReportSection
        id="firms"
        title="Firms"
        description="The window's new leads, per client firm."
        loading={leads.isPending}
        exportData={exports.firms}
        empty={!leads.isPending && (l?.firms.length ?? 0) === 0 ? "No new leads arrived in this window." : false}
      >
        <DataTable
          rows={l?.firms ?? []}
          columns={FIRM_COLUMNS}
          rowKey={(r) => String(r.firm_id)}
          initialSort={{ key: "leads", dir: "desc" }}
          onRowClick={(r) => (r.firm_id ? onDrill({ firms: [r.firm_id] }) : undefined)}
        />
      </ReportSection>

      <ReportSection
        id="compliance"
        title="Compliance"
        description={d ? `The abandonment rule: abandoned over live answers, per campaign, over the 30 days since ${formatDateTime(d.abandonment_since)}. The limit is 3%.` : "The abandonment rule, per campaign."}
        loading={loadingDial}
        exportData={exports.compliance}
      >
        {d && (
          <div className="space-y-5">
            {d.abandonment.length === 0 ? (
              <p className="text-sm text-text-subtle">No live answers in the last 30 days.</p>
            ) : (
              <ul className="space-y-2">
                {d.abandonment.map((a) => (
                  <li key={a.campaign_id ?? "none"} className="grid grid-cols-[minmax(8rem,16rem)_minmax(0,1fr)_7rem] items-center gap-3">
                    <span className="truncate text-sm text-text" title={a.campaign_name ?? undefined}>
                      {a.campaign_name ?? (a.campaign_id ? deletedName(a.campaign_id) : "—")}
                    </span>
                    <Meter value={a.abandon_rate} max={0.1} warnAt={0.02} dangerAt={0.03} limit={0.03} label={`${a.campaign_name ?? "Campaign"} abandon rate`} />
                    <span className="flex items-center justify-end gap-1.5 text-right text-sm tabular-nums">
                      {a.over_limit && <ShieldAlert className="size-3.5 text-danger-11" aria-label="Over the limit" />}
                      <span className={a.over_limit ? "font-semibold text-danger-11" : "text-text"}>{fmtPct(a.abandon_rate)}</span>
                      <span className="text-2xs text-text-subtle">of {fmtCount(a.live_answers)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <Figures
              items={[
                { label: "Hello to a rep", value: fmtSeconds(d.answer_to_rep.p50), hint: `median · 90% within ${fmtSeconds(d.answer_to_rep.p90)}` },
                {
                  label: "Calls refused",
                  value: fmtCount(Object.values(d.blocked).reduce((a, b) => a + b, 0)),
                  hint: Object.entries(d.blocked)
                    .map(([k, n]) => `${labelOf(BLOCK_REASON_LABELS, k)} ${n}`)
                    .join(" · ") || undefined,
                },
                { label: "Reps left with nothing to dial", value: fmtCount(d.problems.length), hint: d.problems.length ? "see below" : "none" },
              ]}
            />
            {d.problems.length > 0 && (
              <ul className="divide-y divide-border rounded-md border border-border">
                {d.problems.map((pr) => (
                  <li key={`${pr.campaign_id}:${pr.rep}:${pr.reason}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
                    <Badge tone="warning" size="sm">
                      {labelOf(PROBLEM_LABELS, pr.reason)}
                    </Badge>
                    <span className="text-text">{pr.rep_name ?? pr.rep}</span>
                    <span className="text-text-muted">{pr.campaign_name ?? deletedName(pr.campaign_id)}</span>
                    <span className="ml-auto text-xs text-text-subtle">
                      {fmtCount(pr.count)} times · last {formatDateTime(pr.last_seen)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </ReportSection>
    </div>
  );
}
